import type { Core, GooglePay, ResultCode, SubmitActions } from '@adyen/adyen-web';
import { createAdyenGooglePayConfiguration, getGooglePayComponentOptions } from '@/utils/adyen';
import type { Customer } from '@/types/checkout';
import type { AdyenGooglePayConfig, CreateGooglePayComponentParams } from '@/types/adyen';
import { Errors } from '@/constants/errors';
import { logError, logInfo, logWarn } from '@/utils/logger';
import { InfoLogs } from '@/constants/logs';

class AdyenExpressService {
  private ExpressCheckoutInstance: Core | null = null;
  private isInitializing = false;
  private googlePayActions: SubmitActions | null = null;

  setGooglePayActions(actions: SubmitActions): void {
    this.googlePayActions = actions;
  }

  // Diagnostic getter for the sheet watchdog (`useAdyenGooglePay`): pending
  // actions past Google's ~30s callback budget mean the sheet WILL die with
  // CALLBACK_TIMED_OUT.
  hasPendingGooglePayActions(): boolean {
    return this.googlePayActions !== null;
  }

  completeGooglePayPayment(success: boolean, resultCode?: ResultCode): void {
    if (!this.googlePayActions) {
      this.logSheetActionWithoutPendingActions('complete', { success, resultCode });
      return;
    }
    if (success && resultCode) {
      this.googlePayActions.resolve({ resultCode });
    } else {
      this.googlePayActions.reject();
    }
    this.googlePayActions = null;
    logInfo(InfoLogs.googlePaySheetSettled, { via: 'complete', success, resultCode });
  }

  // Per Adyen guidance (Adyen/adyen-web#3315): when 3DS is rendered in a
  // custom container via `createFromAction` (our `<AdyenCard3DSForm>`), call
  // `actions.resolve({})` from `onSubmit` to close the wallet sheet without
  // signalling success/failure and without letting the SDK auto-mount its
  // own 3DS2 component. The `CheckoutAdvancedFlowResponse` type says
  // `resultCode` is required, but the SDK accepts an empty object at
  // runtime for exactly this case.
  dismissGooglePaySheet(): void {
    if (!this.googlePayActions) {
      this.logSheetActionWithoutPendingActions('dismiss');
      return;
    }
    (this.googlePayActions.resolve as unknown as (response: Record<string, never>) => void)({});
    this.googlePayActions = null;
    logInfo(InfoLogs.googlePaySheetSettled, { via: 'dismiss' });
  }

  // A settle request with NO pending actions means the sheet promise was
  // never captured (onSubmit never ran) or was already consumed (e.g. the
  // Forter `/3ds/complete` re-entry after the sheet was dismissed, or a
  // non-Adyen Google Pay flow). Warn severity: expected in the re-entry
  // path, but a spike correlated with CALLBACK_TIMED_OUT means a result
  // branch is racing / missing the live sheet.
  private logSheetActionWithoutPendingActions(via: 'complete' | 'dismiss', data?: Record<string, unknown>): void {
    logWarn(InfoLogs.googlePaySheetSettled, { via, noPendingActions: true, ...data });
  }

  async initializeCheckoutInstance(
    config: AdyenGooglePayConfig,
    customer?: Customer,
    paymentMethodConfig?: Record<string, unknown>
  ): Promise<Core> {
    if (this.ExpressCheckoutInstance) {
      return this.ExpressCheckoutInstance;
    }

    if (this.isInitializing) {
      return this.initializeCheckoutInstance(config, customer, paymentMethodConfig);
    }

    this.isInitializing = true;

    try {
      const fullConfig = createAdyenGooglePayConfiguration(config, customer, paymentMethodConfig);

      if (!fullConfig.environment) {
        throw new Error('Adyen environment is required (test or live)');
      }

      const adyenWeb = await import('@adyen/adyen-web');
      const AdyenCheckoutClass = adyenWeb.AdyenCheckout || (adyenWeb as Record<string, unknown>).default;

      if (!AdyenCheckoutClass || typeof AdyenCheckoutClass !== 'function') {
        throw new Error(Errors.payment.failedToImportAdyenCheckout);
      }

      const instance = await AdyenCheckoutClass(fullConfig);
      this.ExpressCheckoutInstance = instance;
      return this.ExpressCheckoutInstance;
    } finally {
      this.isInitializing = false;
    }
  }

  async createGooglePayComponent(params: CreateGooglePayComponentParams): Promise<GooglePay | null> {
    if (!this.ExpressCheckoutInstance) return null;

    const options = getGooglePayComponentOptions(params);

    try {
      const { GooglePay } = await import('@adyen/adyen-web');

      const component = new GooglePay(this.ExpressCheckoutInstance, {
        ...options,
      } as never);
      return component;
    } catch (error) {
      logError('Failed to create Google Pay component', error);
      return null;
    }
  }

  resetGooglePay(): void {
    this.ExpressCheckoutInstance = null;
    this.isInitializing = false;
    this.googlePayActions = null;
  }
}

export const adyenExpressService = new AdyenExpressService();
